import { debounce } from 'lodash';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  ChildrenDefaultProps,
  ChildrenPropTypes,
} from '@/common/proptypes/Base/Children.proptypes';
import { NotFountError } from '@/ebat/api/scan-self-service/getScanSelfServiceProduct';
import { useScanSelfServiceContext } from '@/ebat/contexts/ScanSelfServiceContext/ScanSelfServiceContext';
import useScanSelfServiceAutosuggestions from '@/ebat/queries/scanSelfService/useScanSelfServiceAutosuggestions';

const AUTOSUGGESTIONS_DELAY = 300;
const MIN_SEARCH_TERM_LENGTH = 3;

const ScanSelfServiceAutosuggestionsContext = createContext(null);

const ScanSelfServiceAutosuggestionsProvider = ({ children }) => {
  const { uid } = useParams();
  const {
    error,
    isLoading: isProductLoading,
    hasProduct,
  } = useScanSelfServiceContext();
  const [autosuggestionTerm, setAutosuggestionTerm] = useState('');
  const setTermDebounced = useMemo(
    () =>
      debounce((value) => setAutosuggestionTerm(value), AUTOSUGGESTIONS_DELAY),
    [],
  );
  const hasProductNotFoundError =
    error &&
    error instanceof NotFountError &&
    error.message === autosuggestionTerm;
  const hasProductApiError = error && !(error instanceof NotFountError);

  const queryKey = [`${uid}/products`, autosuggestionTerm];
  const {
    data: products,
    isLoading,
    isFetching,
    isError,
  } = useScanSelfServiceAutosuggestions(
    queryKey,
    isProductLoading ||
      hasProduct ||
      hasProductNotFoundError ||
      hasProductApiError,
    MIN_SEARCH_TERM_LENGTH,
  );
  let errorMessageKey = null;
  if (hasProductNotFoundError) {
    errorMessageKey = 'ebat:entities:scan_self_service.fetch-product-error';
  } else if (hasProductApiError || isError) {
    errorMessageKey = 'ebat:entities:scan_self_service.api-error';
  }
  const isOpen =
    !hasProduct &&
    (isLoading || isFetching || (products && products.length > 0));
  const clearAutosuggestion = () => {
    setAutosuggestionTerm('');
  };

  return (
    <ScanSelfServiceAutosuggestionsContext.Provider
      value={{
        setAutosuggestionTerm: setTermDebounced,
        clearAutosuggestion,
        products: products ?? [],
        isLoading: isLoading || isFetching,
        errorMessageKey,
        isOpen,
      }}
    >
      {children}
    </ScanSelfServiceAutosuggestionsContext.Provider>
  );
};

ScanSelfServiceAutosuggestionsProvider.defaultProps = {
  ...ChildrenDefaultProps,
};

ScanSelfServiceAutosuggestionsProvider.propTypes = {
  ...ChildrenPropTypes,
};

export const useScanSelfServiceAutosuggestionsContext = () =>
  useContext(ScanSelfServiceAutosuggestionsContext);

export default ScanSelfServiceAutosuggestionsProvider;
